<template>
  <div>
    <b-modal v-model="agreementModal" size="lg" ok-title="Continue" :ok-disabled="!acceptedTerms"
             title="Terms and Conditions"
             @ok="createSubscriptionPurchase">
      <div v-html="agreement" class="pb-3"></div>
      <b-form-checkbox v-model="acceptedTerms">
        I accept the Subscription Terms
      </b-form-checkbox>
    </b-modal>

    <form ref="form">
      <b-alert variant="danger" :show="error !== null" v-html="error"></b-alert>

      <b-form-group label="EMAIL ADDRESS" label-class="text-truncate label-required" label-for="input-email">
        <b-form-input v-model="email" required id="input-email" debounce="500"/>
      </b-form-group>

      <b-row>
        <b-col lg="6">
          <b-form-group label="FIRST NAME" label-class="text-truncate label-required">
            <b-input type="text" v-model="firstName" required/>
          </b-form-group>
        </b-col>
        <b-col lg="6">
          <b-form-group label="LAST NAME" label-class="text-truncate label-required">
            <b-input type="text" v-model="lastName" required/>
          </b-form-group>
        </b-col>
      </b-row>

      <organization-billing-form-inputs-slim @organizationParamsChange="organizationAddress = arguments[0]"
                                             @stripeParamsChange="stripeAddress = arguments[0]"/>


      <b-row v-if="isSubscription">
        <b-col cols="auto">
          <b-form-group label="COUPON CODE" label-class="text-truncate">
            <div class="d-flex align-items-center" style="gap: 10px">
              <b-input type="text" v-model="referralCode" debounce="500" :state="codeState"/>
              <span class="text-danger" style="white-space: nowrap" v-if="codeError">{{ codeError }}</span>
              <span class="text-success" style="white-space: nowrap" v-if="codeTerms">{{ codeTerms }}</span>
            </div>
          </b-form-group>
        </b-col>

      </b-row>

      <hr>

      <payment-card-form :hide-name="true" ref="stripeCardForm"></payment-card-form>

      <div class="d-flex justify-content-center">
        <b-button
          class="px-4 py-2 my-3"
          @click="createPurchase"
          variant="primary"
          :disabled="!isValid || saving">
          <span v-if="saving">Please wait...</span>
          <span v-else>{{ buttonTitle || "Purchase" }}</span>
        </b-button>
      </div>
    </form>
  </div>
</template>

<script>
import PaymentCardForm from "../Payments/PaymentCardForm";
import normalize from "json-api-normalize";
import Attributes from "../../../models/Attributes";
import GeneralMixins from "../../../mixins/GeneralMixins";
import OrganizationBillingFormInputsSlim from "./OrganizationBillingFormInputsSlim";

export default {
  components: {OrganizationBillingFormInputsSlim, PaymentCardForm},
  mixins: [GeneralMixins],

  props: [
    'currentProductId', // asset
    'assetId',          // asset
    'productType',
    'selectedSubscription', // subscription
    'agreement',            // subscription
    'buttonTitle',
    'code'
  ],

  data() {
    return {
      firstName: null,
      lastName: null,
      email: null,
      stripeAddress: null,
      organizationAddress: null,
      paymentMethodId: null,
      validEmail: false,
      error: null,
      referralCode: this.code,
      saving: false,
      agreementModal: false,
      acceptedTerms: false,
      codeError: null,
      verifyingCode: false,
      codeTerms: null,
    }
  },

  watch: {
    email(email) {
      const emailRegex = /\S+@\S+\.\S+/;

      if (emailRegex.test(email)) {
        this.verifyEmail(email)
      }
    },

    referralCode(code) {
      if (this.isSubscription) {
        this.codeError = null
        if (code && code.length > 0) {
          this.verifyCode(code)
        }
      }
    }
  },

  computed: {
    codeState() {
      if (this.codeError === null) {
        if (this.verifyingCode === false && this.referralCode && this.referralCode.length > 0) {
          return true
        } else {
          return null
        }
      } else {
        return false
      }
    },
    isSubscription() {
      return this.productType === "subscription"
    },

    ccName() {
      return `${this.firstName || ''} ${this.lastName || ''}`
    },

    isValid() {
      return this.validEmail && this.codeError === null
    },

    userParams() {
      let orgAttributes = this.organizationAddress
      if (this.referralCode && !this.codeError) {
        // this used to be referral_code_number
        orgAttributes = {...orgAttributes, stripe_coupon: this.referralCode, stripe_coupon_description: this.codeTerms}
      }

      return {
        user: {
          email: this.email,
          first_name: this.firstName,
          last_name: this.lastName,
          owned_organization_attributes: orgAttributes
        }
      }
    },

    paymentParams() {
      return {
        payment: {
          product_id: this.currentProductId,
          provisioning_data: {
            asset_id: this.assetId
          }
        },

        user: {
          email: this.email
        }
      }
    },

    subscriptionParams() {
      return {
        price_id: this.selectedSubscription.priceId,
        payment_method_id: this.paymentMethodId,
        user: {
          email: this.email
        }
      }
    },

    paymentMethodParams() {
      return {
        payment_method: {
          stripe_id: this.paymentMethodId
        },

        user: {
          email: this.email
        }
      }
    }
  },

  methods: {
    pollForLink(paymentId) {
      setTimeout(() => {
        this.createDownloadLink(paymentId)
          .then(success => {
            if (success.data && success.data.url) {
              this.$emit("downloadLink", {url: success.data.url})
            } else {
              this.pollForLink(paymentId)
            }
          })
          .catch(error => {
            this.pollForLink(paymentId)
          })
      }, 2000)
    },

    finalizePaymentParams(paymentId) {
      return {
        payment_id: paymentId,
        user: {
          email: this.email
        }
      }
    },

    createDownloadLinkParams(paymentId) {
      return this.finalizePaymentParams(paymentId)
    },

    verifyEmail(email) {
      this.$http.post("/user_verifier_email", {email: email})
        .then(fulfilled => {
          const role = fulfilled.data.user
          if (role === "new") {
            this.validateEmail()
          } else if (role === "customer") {
            this.invalidateEmail("Looks like you already have an account, please <a href='/users/sign-in'>login</a> to complete this transaction</a>")
          } else {
            this.invalidateEmail("You cannot purchase this item using the provided email.")
          }
        }, rejected => {
          this.showErrorToast(this.getErrorMessages(rejected.body));
        })
    },

    verifyCode(code) {
      this.codeError = null
      this.verifyingCode = true

      this.$http.post('/pricing/validate_code', {code: code, price_id: this.selectedSubscription.priceId})
        .then(fulfilled => {
          this.$emit("stripeDiscountChanged", fulfilled.body)
          this.codeTerms = fulfilled.body.terms
          this.verifyingCode = false
        }, rejected => {
          this.verifyingCode = false
          this.$emit("stripeDiscountChanged", null)
          this.codeError = rejected.body.error
          this.codeTerms = null
        })
    },

    invalidateEmail(error) {
      this.validEmail = false
      this.error = error
    },

    validateEmail() {
      this.validEmail = true
      this.error = null
    },

    createPurchase() {
      if (!this.validateLocalForm()) {
        return;
      }

      if (this.productType === "asset") {
        this.createAssetPurchase()
      } else if (this.isSubscription) {
        this.agreementModal = true
      }
    },

    async createAssetPurchase() {
      this.saving = true
      try {
        await this.createUser()
        const payment = await this.createPayment()
        const paymentId = await this.confirmPayment(payment)
        this.$emit("paymentCompleted", {email: this.email})
        this.pollForLink(paymentId)
      } catch (error) {
        this.showErrorToast(this.getErrorMessages(error.body, true))
      } finally {
        this.saving = false
      }
    },

    createSubscriptionPurchase() {
      if (!this.acceptedTerms) {
        this.showErrorToast("You must accept the subscription terms")
        return;
      }

      this.saving = true
      return this.createUser()
        .then(this.createPaymentMethodAndSubscription)
        .catch(error => {
          this.saving = false
          this.showErrorToast(this.getErrorMessages(error.body, true))
        })
    },

    createPayment() {
      return this.$http.post("/public_payments/create_payment_intent", this.paymentParams)
    },

    createUser() {
      return this.$http.post("/public_payments/create_user", this.userParams)
    },

    createSubscription() {
      return this.$http.post("/public_payments/create_subscription", this.subscriptionParams)
    },

    attachPaymentMethod() {
      return this.$http.post("/public_payments/attach_payment_method", this.paymentMethodParams)
    },

    finalizePayment(paymentId) {
      return this.$http.post("/public_payments/finalize_payment", this.finalizePaymentParams(paymentId))
    },

    createDownloadLink(paymentId) {
      return this.$http.post("/public_payments/create_download_link", this.createDownloadLinkParams(paymentId))
    },

    validateLocalForm() {
      if (!this.$refs.form.reportValidity()) {
        this.saving = false
        return
      }

      if (this.$refs.stripeCardForm.localError) {
        this.error = this.$refs.stripeCardForm.localError;
        this.saving = false
        return false;
      }

      if (this.ccName.trim() === '') {
        this.error = 'Name is required.';
        this.saving = false
        return false;
      }

      return true
    },

    async createPaymentMethod() {
      const name = this.ccName;
      const card = this.$refs.stripeCardForm.card;
      const stripe = this.$refs.stripeCardForm.stripe;
      const data = {
        type: 'card',
        card: card,
        billing_details: {
          name: name,
          address: this.stripeAddress
        }
      }

      const result = await stripe.createPaymentMethod(data)

      this.paymentMethodId = result.paymentMethod.id

      if (result.error) {
        throw result.error
      }
    },

    async confirmPayment(createPaymentResponse) {
      const secret = createPaymentResponse.data.client_secret
      const paymentId = createPaymentResponse.data.payment_id

      await this.createPaymentMethod()
      await this.attachPaymentMethod()

      const stripe = this.$refs.stripeCardForm.stripe;
      const data = {
        payment_method: this.paymentMethodId
      }

      const result = await stripe.confirmCardPayment(secret, data)
      if (result.error) {
        throw result.error
      }

      await this.finalizePayment(paymentId)
      return paymentId
    },

    createPaymentMethodAndSubscription() {
      const name = this.ccName;
      const card = this.$refs.stripeCardForm.card;
      const stripe = this.$refs.stripeCardForm.stripe;
      const data = {
        type: 'card',
        card: card,
        billing_details: {
          name: name,
          address: this.stripeAddress
        }
      }

      stripe.createPaymentMethod(data).then((result) => {
        if (result.error) {
          this.error = result.error.message;
          this.saving = false;
        } else {
          this.error = null;
          this.paymentMethodId = result.paymentMethod.id
          this.attachPaymentMethod()
            .then(this.createSubscription)
            .then(success => {
              this.saving = false
              this.$emit("paymentCompleted", {email: this.email})
            })
            .catch(error => {
              this.saving = false
              this.showErrorToast(this.getErrorMessages(error.body, true))
            })
        }
      });
    }

  }
}
</script>